import { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import chevronDown from 'public/static/images/chevron-down-white.svg';
import Dialog from 'components/Dialog';
import LocationAutocomplete from 'components/LocationAutocomplete';
import fetchAutocompleteResults from 'lib/location/Autocomplete';
import { SetLocationInterface, useGeolocationData } from 'lib/hooks/useGeoLocation';
import { CurrentLocation } from 'components/CurrentLocation';
import Prediction from 'lib/location/Locations/Prediction';
import geocodeAndThen from 'lib/location/geocodeAndThen';
import useDeviceScreen from 'lib/hooks/useDeviceScreen';
import { setGeolocationData } from 'redux-implementations/geolocation';
import Geocode from 'types/Geocode';

interface LocationInputProps {
  trackingCategory?: string;
  className?: string;
  onLocationTap?: () => Promise<void>;
  onInputTap?: () => Promise<void>;
  onCurrentLocationTap?: () => Promise<void>;
  onSelectCityTap?: () => Promise<void>;
}

const LocationInput = ({
  trackingCategory,
  className = 'default',
  onLocationTap = (): Promise<void> => Promise.resolve(),
  onInputTap = (): Promise<void> => Promise.resolve(),
  onCurrentLocationTap = (): Promise<void> => Promise.resolve(),
  onSelectCityTap = (): Promise<void> => Promise.resolve(),
}: LocationInputProps): JSX.Element => {
  const geolocation = useGeolocationData();
  const { t } = useTranslation();
  const { isMobile } = useDeviceScreen();
  const dispatch = useDispatch();

  const setGeolocation: SetLocationInterface = async (location: Geocode): Promise<void> => {
    dispatch(setGeolocationData(location.toJSON()));
  };

  function createOnSelect(hide: () => void): (pred: Prediction) => Promise<void> {
    return async function onSelect(pred: Prediction): Promise<void> {
      return geocodeAndThen(pred, async (result) => {
        hide();
        await setGeolocation(result);
      });
    };
  }

  function createOnLocate(hide: () => void): (location: Geocode) => Promise<void> {
    return async function onLocate(location: Geocode): Promise<void> {
      hide();
      await setGeolocation(location);
    };
  }

  // this prevents the mobile keyboard from pushing content up out of the screen. For some reason, class-based styles don't work
  const contentWrapperStyles: CSSProperties | undefined = isMobile
    ? { position: 'fixed', top: 0, width: '100%' }
    : undefined;

  return (
    <>
      <Dialog
        button={({ toggle }): JSX.Element => (
          <button
            className={`location-input-dialog-button ${className}`}
            role="button"
            data-location-input
            onClick={(): void => {
              toggle();
              onLocationTap();
            }}
          >
            {geolocation &&
              (geolocation.latitude === 0 && geolocation.longitude === 0
                ? t('home.location')
                : `${geolocation.city}, ${geolocation.stateAbbreviation}`)}
            {className.includes('paid-location-input') && <span className="location-input-caret" />}
          </button>
        )}
        content={({ hide }): JSX.Element => (
          <div style={contentWrapperStyles}>
            <LocationAutocomplete<Prediction>
              needsAttribution={false}
              fetchResults={fetchAutocompleteResults}
              onSelect={createOnSelect(hide)}
              currentLocation={
                <CurrentLocation
                  onCurrentLocationTap={onCurrentLocationTap}
                  onLocated={createOnLocate(hide)}
                  trackingCategory={trackingCategory}
                />
              }
              onInputTap={onInputTap}
              onSelectCityTap={onSelectCityTap}
            />
          </div>
        )}
      />
      <style jsx>{`
        .location-input-dialog-button {
          display: flex;
          justify-content: center;
          align-items: center;
          border: solid 1px transparent;
          background: transparent;
          padding: 0;
          margin: 0;
        }

        .location-input-dialog-button:focus {
          border: solid 1px;
        }

        .default {
          background: white;
          border-radius: 4px;
          color: black;
          font-weight: 300;
          font-size: 16px;
          cursor: text;
          height: 39px;
          padding: 8px 10px;
          width: 100%;
          text-align: center;
          margin: 5px 0 20px 0;
        }

        .location-input-caret {
          width: 30px;
          height: 30px;
          margin-right: -6px;
          background-image: url(${chevronDown});
          background-size: cover;
        }

        .location-input-location-autocomplete-outer {
          display: fixed;
          top: 0;
          width: 100%;
        }
      `}</style>
    </>
  );
};

export default LocationInput;
